import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { queryClient } from '@/lib/query-client';
import { netUnitCostHt } from '@erp/domain/sales';
import type {
  Article,
  IDataService,
  ProArticlePricingRow,
} from '@erp/data-access/interfaces';
import type { ProArticle } from '@erp/domain/pro';

const PRO_ARTICLE_FETCH_BATCH_SIZE = 1000;

// The catalogue is read-mostly tenant data; keep it fresh for 5 min and alive
// in memory for a day so the persisted (IndexedDB) copy stays warm across
// navigations instead of being garbage-collected after the default gcTime.
const CATALOG_STALE_TIME = 5 * 60_000;
const CATALOG_GC_TIME = 24 * 60 * 60_000;

// Shared pricing query — the SAME key/fn is used by `useProArticlePricing`
// and (via ensureQueryData) inside `useProArticles`, so the pro_article_pricing
// list is fetched exactly ONCE per load instead of twice.
function pricingQueryOptions(ds: IDataService) {
  return {
    queryKey: ['pro-article-pricing'] as const,
    queryFn: (): Promise<ProArticlePricingRow[]> => ds.proArticlePricing.listAll(),
    staleTime: CATALOG_STALE_TIME,
    gcTime: CATALOG_GC_TIME,
  };
}

/**
 * Maps a catalog article to the ProSpace view.
 *
 * The publication row (`pro_article_pricing`) may carry a ProSpace-only title,
 * description and reference; `null`/absent means "follow the article", so a
 * rename in the dashboard propagates on its own until someone writes a Pro
 * label. Applying the override here means every surface — cards, detail modal,
 * search, cart snapshots, checkout — gets it for free.
 */
export function articleToProArticle(
  a: Article,
  // Structural, not `Pick<ProArticlePricingRow, …>`: callers hand over either
  // the repository row or the domain `ProArticlePricing` (whose override
  // fields are optional), and both must fit.
  pricing?: {
    nameFr?: string | null;
    descriptionFr?: string | null;
    reference?: string | null;
  } | null,
): ProArticle {
  return {
    id: a.id,
    code: pricing?.reference?.trim() || a.code,
    name: pricing?.nameFr?.trim() || a.name,
    description: pricing?.descriptionFr?.trim() || a.description,
    imageUrl: a.imageUrl ?? (a.images?.[0] ?? null),
    salePriceHt: a.salePriceHt ?? 0,
    vatRate: a.vatRate ?? 19,
    // Coût net HT (remise fournisseur déduite) — base de la commission pro.
    purchasePrice: netUnitCostHt(a) ?? 0,
    categoryId: a.categoryId,
    subFamily: a.subFamily,
    familyId: a.family,
    brandId: a.brand,
    isActive: a.isActive ?? true,
    images: a.images ?? [],
    unit: a.unit,
    barcode: a.barcode,
  };
}

async function fetchActiveArticles(ds: IDataService): Promise<Article[]> {
  // Page directly without a preliminary count() — for the common case of a
  // sub-1000-article catalogue that's a single round-trip instead of two.
  const articles: Article[] = [];
  for (let offset = 0; ; offset += PRO_ARTICLE_FETCH_BATCH_SIZE) {
    const batch = await ds.articles.list({
      isActive: true,
      offset,
      limit: PRO_ARTICLE_FETCH_BATCH_SIZE,
    });
    articles.push(...batch);
    if (batch.length < PRO_ARTICLE_FETCH_BATCH_SIZE) break;
  }
  return articles;
}

export function useProArticles(opts?: { enabled?: boolean }) {
  const ds = useDataService();
  return useQuery({
    enabled: opts?.enabled ?? true,
    queryKey: ['pro-articles'],
    queryFn: async (): Promise<ProArticle[]> => {
      // Only active articles explicitly published to ProSpace are visible.
      // A pro_article_pricing row is the publish/hide switch and also carries
      // the min/moy/max discount overrides. `ensureQueryData` reuses (or
      // populates) the shared ['pro-article-pricing'] cache entry so the
      // pricing list isn't fetched a second time by useProArticlePricing.
      const [articles, pricing] = await Promise.all([
        fetchActiveArticles(ds),
        queryClient.ensureQueryData(pricingQueryOptions(ds)),
      ]);
      const pricingByArticle = new Map(pricing.map((p) => [p.articleId, p]));
      return articles
        .filter((article) => pricingByArticle.has(article.id))
        .map((article) => articleToProArticle(article, pricingByArticle.get(article.id)));
    },
    staleTime: CATALOG_STALE_TIME,
    gcTime: CATALOG_GC_TIME,
    // Keep the previous catalogue on screen while a background refresh runs,
    // so returning to the page never flashes the skeleton.
    placeholderData: keepPreviousData,
  });
}

export function useProCategories() {
  const ds = useDataService();
  return useQuery({
    queryKey: ['pro-article-categories'],
    queryFn: () => ds.articleCategories.list(),
    staleTime: 5 * 60_000,
  });
}

/** Published ProSpace categories — the publishing layer that drives the
 *  catalog filter. Only categories published from the dashboard appear. */
export function useProPublishedCategories() {
  const ds = useDataService();
  return useQuery({
    queryKey: ['pro-published-categories'],
    queryFn: () => ds.proCategories.listPublished(),
    staleTime: 5 * 60_000,
  });
}

export function useProFamilies() {
  const ds = useDataService();
  return useQuery({
    queryKey: ['pro-article-families'],
    queryFn: () => ds.articleFamilies.list(),
    staleTime: 5 * 60_000,
  });
}

export function useProArticlePricing(opts?: { enabled?: boolean }) {
  const ds = useDataService();
  return useQuery({
    ...pricingQueryOptions(ds),
    enabled: opts?.enabled ?? true,
    placeholderData: keepPreviousData,
  });
}

export function useProFavorites() {
  const ds = useDataService();
  return useQuery({
    queryKey: ['pro-favorites'],
    queryFn: () => ds.proFavorites.list(),
    staleTime: 30_000,
  });
}
